import { Box } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import type {
  FacilityResponce,
  ForgotPassword as ForgotPasswordType,
} from '../../API/services/loginServices';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import QLerLogo from '@assets/images/qler_Logo.svg';
import type { AlertColor } from '@mui/material';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFacilityForgotPasswordEmail } from '../../API/hooks/loginHook';
const initialValues: ForgotPasswordType = {
  username: '',
  email: '',
};
export interface ForgotPasswordScreenProps {
  handleSubmitData: (data: ForgotPasswordType) => void;
  setToLogin: () => void;
}

const validationSchema = Yup.object({
  username: Yup.string().required('Username is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});
const ForgotPassword = () => {
  const { mutate: mutateForgotPassword } = useFacilityForgotPasswordEmail();
  const MessageRef = useRef('');
  const [messageType, setMessageType] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const navigate = useNavigate();

  const handleForgotPassword = (request: ForgotPasswordType) => {
    mutateForgotPassword(
      {
        username: request.username,
        email: request.email,
      },
      {
        onSuccess: (data) => {
          MessageRef.current = data.message;
          setMessageType('success');
          setOpenToast(true);
          setTimeout(() => {
            navigate('/', { replace: true });
            setOpenToast(false);
            MessageRef.current = '';
          }, 3000);
        },

        onError: (error: Error | FacilityResponce) => {
          setMessageType('error');
          MessageRef.current = error.message;
          setOpenToast(true);
          setTimeout(() => {
            setOpenToast(false);
            MessageRef.current = '';
          }, 8000);
        },
      }
    );
  };
  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box className="container">
      {openToast && (
        <CustomSnackbar
          id="login-toast"
          successOrError={messageType as AlertColor}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={MessageRef.current}
        />
      )}
      <Box className="center-div">
        <Box className="logo-qler">
          <QLerLogo />
        </Box>
        <Formik
          initialValues={initialValues}
          validateOnMount={true}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleForgotPassword(values);
          }}
        >
          {({ handleSubmit, setFieldValue, errors, touched, values }) => (
            <Grid2 container spacing={2} justifyContent="center">
              <CustomCard
                className="p-16 card-width login-form"
                id="cardWithoutImage"
              >
                <CustomTypography className="pb-12" id="demo-h2" variant="h3">
                  Forgot Password?
                </CustomTypography>
                <CustomTypography
                  id="demo-h2"
                  variant="body2"
                  color="textSecondary"
                  className="pb-32"
                >
                  Please enter your username and email to reset your password.
                </CustomTypography>

                <Box display="flex" justifyContent="center" className="pb-16">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomTextField
                      id="txtName"
                      placeholder="Username"
                      size="small"
                      type="text"
                      className="input-width"
                      value={values.username}
                      onChange={(e) =>
                        setFieldValue('username', e.target.value)
                      }
                    />
                    {errors.username && touched.username && (
                      <CustomTypography
                        id="error-username"
                        variant="body3"
                        color="error"
                        className="float-left"
                      >
                        <ErrorMessage name="username" />
                      </CustomTypography>
                    )}
                  </Grid2>
                </Box>
                <Box display="flex" justifyContent="center" className="pb-24">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomTextField
                      id="txtName"
                      placeholder="Email"
                      size="small"
                      type="text"
                      className="input-width"
                      value={values.email}
                      onChange={(e) => setFieldValue('email', e.target.value)}
                      minLength={2}
                      maxLength={10}
                    />
                    {errors.email && touched.email && (
                      <CustomTypography
                        id="error-password"
                        variant="body3"
                        color="error"
                        className="float-left"
                      >
                        <ErrorMessage name="email" />
                      </CustomTypography>
                    )}
                  </Grid2>
                </Box>

                <Box display="flex" justifyContent="center" className="pb-24">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomButton
                      id="btnLarge"
                      variant="contained"
                      size="large"
                      className="button-width button-height pt-28 button-font-size"
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </CustomButton>
                  </Grid2>
                </Box>

                <Box display="flex" justifyContent="center">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <CustomLink
                      id="demo-link"
                      color="info"
                      variant="h6"
                      underline="none"
                      onClick={() => {
                        setOpenToast(false);
                        setMessageType('');
                        MessageRef.current = '';
                        navigate('/', { replace: true });
                      }}
                    >
                      Back to Login
                    </CustomLink>
                  </Grid2>
                </Box>
                <CustomDivider className="pt-24" />
                <CustomTypography
                  id="demo-h2"
                  variant="body2"
                  color="textSecondary"
                  className="pt-24"
                >
                  Having Difficulty? Please call
                </CustomTypography>
                <CustomTypography id="demo-h2" variant="h6">
                  (855) 797-7879
                </CustomTypography>
              </CustomCard>
            </Grid2>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
